import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { SunMap } from '../components/SunMap';
import { AIGuidanceCard } from '../components/AIGuidanceCard';
import { FeedbackSurvey } from './FeedbackSurvey';
import { DictationButton } from '../components/DictationButton';
import api, { getAuthToken } from '../utils/api';
import { toast } from 'sonner';
import { 
  Loader2, LogOut, Sparkles, Library, 
  Sun, Moon, Settings, Target, ChevronRight, 
  BarChart3, Home, ChevronDown, RefreshCw, FileDown, Share2,
  Eye, EyeOff, AlertTriangle, Clock, Smartphone, Cloud, 
  UserMinus, Shuffle, Lock, Zap, TrendingUp, TrendingDown, Compass,
  CheckCircle2, Circle, Plus, X, Rocket, Flame, Sunrise, Play, Check
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { exportAnalysisPDF } from '../utils/exportUtils';
import DashboardTour from '../components/DashboardTour';
import SignalBar from '../components/SignalBar';
import TodayPath from '../components/TodayPath';

// Resistance pattern config
// Resistance pattern configurations - Oracle-like names
const RESISTANCE_PATTERNS = {
  procrastination: { icon: Clock, color: '#F59E0B', label: { en: 'The Waiting One', tr: 'Bekleyen' } },
  emotional_resistance: { icon: EyeOff, color: '#8B5CF6', label: { en: 'The Guarded Heart', tr: 'Korunan Kalp' } },
  overexertion: { icon: Zap, color: '#EF4444', label: { en: 'The Restless Builder', tr: 'Huzursuz İnşaatçı' } },
  doom_scrolling: { icon: Smartphone, color: '#6B7280', label: { en: 'The Numb Wanderer', tr: 'Uyuşmuş Gezgin' } },
  fantasy_without_execution: { icon: Cloud, color: '#3B82F6', label: { en: 'The Dreamer Unlanded', tr: 'Yere İnmeyen Hayalci' } },
  relational_withdrawal: { icon: UserMinus, color: '#EC4899', label: { en: 'The One Who Retreats', tr: 'Geri Çekilen' } },
  perfectionism: { icon: Target, color: '#14B8A6', label: { en: 'The Never-Ready', tr: 'Asla Hazır Olmayan' } },
  scattered_attention: { icon: Shuffle, color: '#F97316', label: { en: 'The Surface Skimmer', tr: 'Yüzey Kayıcı' } },
  control_obsession: { icon: Lock, color: '#64748B', label: { en: 'The Tight Grip', tr: 'Sıkı Tutuş' } },
  pleasure_seeking: { icon: Sparkles, color: '#A855F7', label: { en: 'The Sweet Escape', tr: 'Tatlı Kaçış' } },
};

// Domain labels for clickable cards
const DOMAIN_LABELS = {
  en: {
    physical_health: 'Physical Health',
    strength_fitness: 'Strength / Fitness',
    energy: 'Energy',
    environment_order: 'Space / Order',
    work_mission: 'Work / Mission',
    money_financial: 'Money / Financial',
    love_relationship: 'Love / Relationship',
    friends_community: 'Friends / Community',
    emotional_stability: 'Emotional Stability',
    discipline_consistency: 'Discipline',
    learning_growth: 'Learning / Growth',
    meaning_purpose: 'Meaning / Purpose',
  },
  tr: {
    physical_health: 'Fiziksel Sağlık',
    strength_fitness: 'Güç / Fitness',
    energy: 'Enerji',
    environment_order: 'Alan / Düzen',
    work_mission: 'İş / Misyon',
    money_financial: 'Para / Finansal',
    love_relationship: 'Aşk / İlişki',
    friends_community: 'Arkadaşlar / Topluluk',
    emotional_stability: 'Duygusal Denge',
    discipline_consistency: 'Disiplin',
    learning_growth: 'Öğrenme / Gelişim',
    meaning_purpose: 'Anlam / Amaç',
  },
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, changeLanguage } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [userName, setUserName] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sharing, setSharing] = useState(false);
  
  // Mission Control integrations
  const [solarAlignment, setSolarAlignment] = useState(null);
  const [resistance, setResistance] = useState(null);
  const [resistanceLoading, setResistanceLoading] = useState(false);
  const [showShadowPanel, setShowShadowPanel] = useState(false);
  const [navigatorFeed, setNavigatorFeed] = useState(null);
  const [navigatorLoading, setNavigatorLoading] = useState(false);
  
  // Active Missions state
  const [missions, setMissions] = useState([]);
  const [carriedOverMissions, setCarriedOverMissions] = useState([]);
  const [showCarriedOver, setShowCarriedOver] = useState(false);
  const [missionStats, setMissionStats] = useState({ total: 0, completed: 0, completion_rate: 0 });
  const [missionSuggestions, setMissionSuggestions] = useState([]);
  const [showAddMission, setShowAddMission] = useState(false);
  const [newMissionText, setNewMissionText] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  const [selectedFrequency, setSelectedFrequency] = useState('weekly');
  
  // Win streak
  const [winStreak, setWinStreak] = useState(null);
  
  // Today's morning data (for Navigator panel)
  const [todayMorning, setTodayMorning] = useState(null);
  const [todayMorningLoading, setTodayMorningLoading] = useState(true);
  
  // Dashboard Tour
  const [showTour, setShowTour] = useState(false);
  
  const [addingMission, setAddingMission] = useState(false);

  // Export Sun Map and Analysis as PDF
  const handleExportPDF = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const { assessment, analysis } = data;
      await exportAnalysisPDF({
        scores: assessment.scores,
        meta_scores: assessment.meta_scores,
        ai_analysis: {
          main_reading: analysis.analysis_text,
          archetypes: analysis.archetypes,
          priorities: analysis.priorities,
          weekly_focus: analysis.weekly_focus,
          habits: analysis.habits,
        },
        userName: userName,
        date: assessment.timestamp || data.created_at,
      }, language);
      toast.success(language === 'tr' ? 'PDF indirildi!' : 'PDF downloaded!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(language === 'tr' ? 'Dışa aktarma başarısız' : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Share Sun Map link
  const handleShare = async () => {
    setSharing(true);
    try {
      const response = await api.post('/assessment/share', {});
      const shareId = response.data.share_id;
      const shareUrl = `${window.location.origin}/shared/${shareId}`;
      
      const shareText = language === 'tr' 
        ? `${(userName || 'Benim').charAt(0).toUpperCase() + (userName || 'Benim').slice(1)} Güneş Haritam - IN İç Navigasyon` 
        : `${userName ? `${userName.charAt(0).toUpperCase() + userName.slice(1)}'s` : 'My'} Sun Map - IN Inner Navigation`;

      // Try native share
      if (navigator.share) {
        try {
          await navigator.share({
            title: shareText,
            text: `${shareText}\n${language === 'tr' ? 'Kendi haritanı oluştur:' : 'Create your own:'}`,
            url: shareUrl,
          });
          toast.success(language === 'tr' ? 'Paylaşıldı!' : 'Shared!');
          return;
        } catch (e) {
          if (e.name === 'AbortError') return;
        }
      }
      
      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(shareUrl);
      toast.success(language === 'tr' ? 'Bağlantı kopyalandı!' : 'Link copied!');
    } catch (error) {
      console.error('Share error:', error);
      toast.error(language === 'tr' ? 'Paylaşım başarısız' : 'Share failed');
    } finally {
      setSharing(false);
    }
  };

  // Mission handlers
  const fetchMissions = async () => {
    try {
      const response = await api.get('/missions/active');
      setMissions(response.data.missions || []);
      setCarriedOverMissions(response.data.carried_over || []);
      setMissionStats(response.data.stats || { total: 0, completed: 0, completion_rate: 0 });
    } catch (error) {
      console.error('Failed to fetch missions:', error);
    }
  };

  const fetchMissionSuggestions = async () => {
    try {
      const response = await api.get('/missions/suggestions');
      setMissionSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch mission suggestions:', error);
    }
  };

  const handleAddMission = async (text, domain, frequency = 'weekly') => {
    if (!text.trim() || !domain) return;
    setAddingMission(true);
    try {
      await api.post('/missions', { text: text.trim(), domain, frequency });
      await fetchMissions();
      await fetchSolarAlignment();
      setNewMissionText('');
      setSelectedDomain('');
      setShowAddMission(false);
      toast.success(language === 'tr' ? 'Misyon eklendi!' : 'Mission added!');
    } catch (error) {
      toast.error(language === 'tr' ? 'Misyon eklenemedi' : 'Failed to add mission');
    } finally {
      setAddingMission(false);
    }
  };

  const handleToggleMission = async (mission) => {
    try {
      const endpoint = mission.status === 'completed' 
        ? `/missions/${mission.id}/uncomplete`
        : `/missions/${mission.id}/complete`;
      await api.patch(endpoint);
      await fetchMissions();
      await fetchSolarAlignment(); // Refresh alignment score
      
      if (mission.status !== 'completed') {
        toast.success(language === 'tr' ? 'Misyon tamamlandı!' : 'Mission completed!');
      }
    } catch (error) {
      toast.error(language === 'tr' ? 'Güncelleme başarısız' : 'Update failed');
    }
  };

  const handleDeleteMission = async (missionId) => {
    try {
      await api.delete(`/missions/${missionId}`);
      await fetchMissions();
      await fetchSolarAlignment();
      toast.success(language === 'tr' ? 'Misyon silindi' : 'Mission deleted');
    } catch (error) {
      toast.error(language === 'tr' ? 'Silme başarısız' : 'Delete failed');
    }
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Initial data fetch
  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      navigate('/auth');
      return;
    }
    fetchData();
    checkFeedbackStatus();
    fetchSolarAlignment();
    fetchMissions();
    fetchMissionSuggestions();
    fetchWinStreak();
    fetchTodayMorning();
    // Check if tour should be shown
    if (localStorage.getItem('in_show_tour') === 'true' && !localStorage.getItem('in_tour_completed')) {
      setTimeout(() => setShowTour(true), 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch missions when returning to dashboard (location changes)
  useEffect(() => {
    if (location.pathname === '/dashboard') {
      fetchMissions();
      fetchSolarAlignment();
      fetchWinStreak();
      fetchTodayMorning();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Fetch Solar Alignment score
  const fetchSolarAlignment = async () => {
    try {
      const response = await api.get('/assessment/solar-alignment');
      if (response.data?.current?.solar_alignment) {
        const solar = response.data.current.solar_alignment;
        setSolarAlignment({
          score: solar.score,
          interpretation: solar.interpretation,
          trend: response.data.trajectory?.trend || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch solar alignment:', error);
    }
  };

  // Fetch Win Streak
  const fetchWinStreak = async () => {
    try {
      const response = await api.get('/checkin/win-streak');
      setWinStreak(response.data);
    } catch (error) {
      // No streak data yet is fine
    }
  };

  // Fetch Today's Morning Check-in
  const fetchTodayMorning = async () => {
    setTodayMorningLoading(true);
    try {
      const response = await api.get('/checkin/morning/today');
      setTodayMorning(response.data);
    } catch (error) {
      // Not checked in yet
    } finally {
      setTodayMorningLoading(false);
    }
  };


  // Fetch Shadow/Resistance Detection
  const fetchResistance = async () => {
    setResistanceLoading(true);
    try {
      const response = await api.get('/assessment/resistance-detection');
      setResistance(response.data);
    } catch (error) {
      console.error('Failed to fetch resistance:', error);
    } finally {
      setResistanceLoading(false);
    }
  };

  // Fetch Navigator Feed (AI coach commands)
  const fetchNavigatorFeed = async () => {
    setNavigatorLoading(true);
    try {
      const response = await api.get('/coaching/navigator-feed');
      setNavigatorFeed(response.data);
    } catch (error) {
      console.error('Failed to fetch navigator feed:', error);
      // Set fallback
      setNavigatorFeed({
        todays_orientation: language === 'tr' 
          ? 'Bugün için henüz bir yönlendirme yok. Check-in yaparak başlayın.'
          : 'No orientation yet for today. Start with a check-in.',
        primary_tension: null,
        next_best_move: null,
        question_of_day: language === 'tr'
          ? 'Bugün neye odaklanmak istiyorsun?'
          : 'What do you want to focus on today?'
      });
    } finally {
      setNavigatorLoading(false);
    }
  };

  const checkFeedbackStatus = async () => {
    // Feedback survey disabled
    return;
  };

  const fetchData = async () => {
    try {
      const [currentResponse, historyResponse, userResponse] = await Promise.all([
        api.get('/assessment/current'),
        api.get('/assessment/history'),
        api.get('/auth/me'),
      ]);
      setData(currentResponse.data);
      setHistory(historyResponse.data);
      setUserName(userResponse.data.name || '');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Please complete your assessment first');
        navigate('/assessment');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateAnalysis = async () => {
    setRegenerating(true);
    try {
      const response = await api.post('/assessment/regenerate-analysis');
      setData(response.data);
      toast.success(language === 'tr' ? 'Okuma Türkçeye çevrildi!' : 'Reading translated successfully!');
    } catch (error) {
      toast.error(language === 'tr' ? 'Çeviri başarısız oldu' : 'Translation failed');
    } finally {
      setRegenerating(false);
    }
  };

  const handleFeedbackComplete = () => {
    setShowFeedbackModal(false);
    setFeedbackStatus({ ...feedbackStatus, survey_completed: true });
    toast.success(language === 'tr' ? 'Geri bildiriminiz için teşekkürler!' : 'Thank you for your feedback!');
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030305]">
        <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { assessment, analysis } = data;
  const previousAssessment = history.length > 1 ? history[1] : null;

  // Quick action items
  const quickActions = [
    { icon: Sparkles, label: language === 'tr' ? 'Navigatör' : 'Navigator', path: '/coaching', primary: true },
    { icon: Sun, label: language === 'tr' ? 'Sabah' : 'Morning', path: '/morning' },
    { icon: Moon, label: language === 'tr' ? 'Akşam' : 'Evening', path: '/evening' },
    { icon: Compass, label: language === 'tr' ? 'Ritüeller' : 'Rituals', path: '/integrate' },
    { icon: Library, label: 'The INN', path: '/inn' },
  ];

  return (
    <div className="min-h-screen bg-[#030305] relative pb-32 lg:pb-16">
      {/* Dashboard Tour */}
      {showTour && <DashboardTour onComplete={() => setShowTour(false)} />}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackSurvey isModal={true} onComplete={handleFeedbackComplete} />
      )}

      {/* Background subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 via-transparent to-transparent pointer-events-none" />
      
      {/* Top Navigation Bar */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/5"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <span className="font-serif text-xl text-[#D4AF37]">IN</span>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => changeLanguage(language === 'en' ? 'tr' : 'en')}
              className="w-9 h-9 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:text-[#D4AF37] hover:bg-white/5 transition-all text-xs font-sans font-semibold"
            >
              {language === 'en' ? 'TR' : 'EN'}
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="w-9 h-9 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:text-[#D4AF37] hover:bg-white/5 transition-all"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              onClick={handleLogout}
              className="w-9 h-9 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:text-red-400 hover:bg-white/5 transition-all"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main
        className="px-6"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 5rem)' }}
      >
        <div className="max-w-4xl mx-auto flex flex-col">
          {/* TODAY panel — pulled to top so Today's Direction is the first thing visible */}
          <div data-testid="today-panel-anchor-top" />

          {/* Solar Streak + Alignment Info Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="flex flex-wrap items-center justify-center gap-3 mb-6"
          >
            {/* Solar Alignment Details (interpretation + trend) */}
            {solarAlignment && (
              <div className="inline-flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5">
                <p className="text-sm text-[#F8F5F2] font-sans">{solarAlignment.interpretation?.label || ''}</p>
                {solarAlignment.trend !== 0 && (
                  <p className={`text-xs flex items-center gap-1 ${solarAlignment.trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {solarAlignment.trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {solarAlignment.trend > 0 ? '+' : ''}{solarAlignment.trend}
                  </p>
                )}
              </div>
            )}

            {/* Win Streak Badge */}
            {winStreak && winStreak.current_streak > 0 && (() => {
              const s = winStreak.current_streak;
              const isMilestone = s === 3 || s === 7 || s === 14 || s === 21 || s === 30 || s === 60 || s === 90 || s === 100 || s === 365;
              const isHot = s >= 7;
              const milestoneLabel = s >= 365 ? (language === 'tr' ? 'Efsane!' : 'Legendary!') 
                : s >= 90 ? (language === 'tr' ? 'Durdurulamaz!' : 'Unstoppable!') 
                : s >= 30 ? (language === 'tr' ? 'Bir ay!' : 'One month!') 
                : s >= 14 ? (language === 'tr' ? 'İki hafta!' : 'Two weeks!') 
                : s >= 7 ? (language === 'tr' ? 'Bir hafta!' : 'One week!') 
                : s >= 3 ? (language === 'tr' ? 'Momentum!' : 'Momentum!') : null;
              return (
                <div
                  className={`inline-flex items-center gap-2 rounded-2xl px-5 py-3 cursor-pointer transition-all duration-300 ${
                    isMilestone 
                      ? 'bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 border-2 border-amber-400/60 shadow-[0_0_20px_rgba(245,158,11,0.3)] animate-pulse'
                      : isHot
                      ? 'bg-gradient-to-r from-amber-500/15 to-orange-500/15 border border-amber-500/40 shadow-[0_0_12px_rgba(245,158,11,0.15)]'
                      : 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30'
                  } hover:shadow-[0_0_25px_rgba(245,158,11,0.35)]`}
                  onClick={() => navigate('/evening')}
                  data-testid="win-streak-badge"
                >
                  <Flame className={`w-5 h-5 ${isMilestone ? 'text-orange-300' : 'text-orange-400'}`} />
                  <div className="text-center">
                    <p className={`text-2xl font-serif ${isMilestone ? 'text-amber-200' : 'text-amber-300'}`}>{s}</p>
                    <p className="text-[10px] tracking-wider uppercase text-amber-400/70 font-sans">
                      {language === 'tr' ? 'solar seri' : 'solar streak'}
                    </p>
                  </div>
                  <Flame className={`w-5 h-5 ${isMilestone ? 'text-orange-300' : 'text-orange-400'}`} />
                  {milestoneLabel && (
                    <span className="text-[10px] font-sans font-semibold text-amber-300 tracking-wide ml-1">
                      {milestoneLabel}
                    </span>
                  )}
                </div>
              );
            })()}

          </motion.div>

          {/* Quick Actions Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="grid grid-cols-5 gap-3 mb-6"
          >
            {quickActions.map((action, index) => (
              <button
                key={action.path}
                onClick={() => navigate(action.path)}
                className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all duration-300 ${
                  action.primary
                    ? 'bg-[#D4AF37] text-[#030305] shadow-[0_0_20px_rgba(212,175,55,0.3)]'
                    : 'bg-white/5 border border-white/10 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/30'
                }`}
                data-testid={`quick-action-${action.path.slice(1)}`}
              >
                <action.icon className="w-6 h-6 mb-2" />
                <span className="text-xs font-sans">{action.label}</span>
              </button>
            ))}
          </motion.div>

          {/* TODAY — The Navigator (Core Interface) — pulled to top via flex order */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.42 }}
            className="mb-6 order-first"
            data-testid="navigator-today-panel"
          >
            {todayMorningLoading ? (
              <div className="glass-panel rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Compass className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
                </div>
              </div>
            ) : todayMorning?.submitted ? (
              /* MORNING DONE — Today's Direction is the FIRST content (the banner).
                 Above it: only buttons + symbols (Compass icon + Weekly Navigator link).
                 Below it: daily state, pills, micro-action — supporting detail. */
              <div className="rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-b from-[#D4AF37]/5 to-transparent overflow-hidden">
                {/* Header — buttons + symbols only, no text label */}
                <div className="px-6 pt-5 pb-3 flex items-center justify-between">
                  <Compass className="w-5 h-5 text-[#D4AF37]" data-testid="today-compass-icon" />
                  <button
                    onClick={() => navigate('/navigator')}
                    className="text-[#6B7280] hover:text-[#D4AF37] transition-colors flex items-center gap-1"
                    data-testid="navigator-full-link"
                    title={language === 'tr' ? 'Haftalık Navigator' : 'Weekly Navigator'}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                {/* TODAY'S DIRECTION — The Banner (FIRST visible content under the icons) */}
                {todayMorning.one_win && (
                  <div
                    className={`mx-6 mb-4 p-4 rounded-xl transition-all ${
                      todayMorning.direction_achieved
                        ? 'bg-emerald-500/10 border border-emerald-500/40'
                        : 'bg-[#D4AF37]/10 border border-[#D4AF37]/30'
                    }`}
                    data-testid="today-direction"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/70 font-sans mb-2">
                          {language === 'tr' ? 'Bugünün Yönü' : "Today's Direction"}
                        </p>
                        <p
                          className={`font-serif text-xl leading-snug ${
                            todayMorning.direction_achieved
                              ? 'text-emerald-300 line-through decoration-emerald-400/40'
                              : 'text-[#F8F5F2]'
                          }`}
                          data-testid="today-one-win"
                        >
                          {todayMorning.one_win}
                        </p>
                        {todayMorning.direction_achieved && (
                          <p className="text-[11px] text-emerald-300/80 font-sans mt-2">
                            {language === 'tr' ? 'Tamamlandı. Güzel.' : 'Marked complete. Well done.'}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={async () => {
                          const newVal = !todayMorning.direction_achieved;
                          setTodayMorning(prev => prev ? { ...prev, direction_achieved: newVal } : prev);
                          if (newVal) window.dispatchEvent(new Event('signal:celebrate'));
                          try {
                            await api.post('/checkin/direction/mark', { achieved: newVal });
                            toast.success(newVal
                              ? (language === 'tr' ? 'Yön tamamlandı' : 'Direction complete')
                              : (language === 'tr' ? 'Geri alındı' : 'Unmarked'));
                          } catch (error) {
                            setTodayMorning(prev => prev ? { ...prev, direction_achieved: !newVal } : prev);
                            toast.error(language === 'tr' ? 'Kaydedilemedi' : 'Failed to save');
                          }
                        }}
                        className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                          todayMorning.direction_achieved
                            ? 'bg-emerald-500/30 border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/40'
                            : 'bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/20'
                        }`}
                        title={todayMorning.direction_achieved
                          ? (language === 'tr' ? 'Tamamlandı — geri al' : 'Completed — undo')
                          : (language === 'tr' ? 'Tamamlandı olarak işaretle' : 'Mark complete')}
                        data-testid="today-direction-check"
                      >
                        <Check className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Supporting detail BELOW the banner */}
                <div className="px-6 pb-3">
                  {todayMorning.output?.reflection && (
                    <p className="text-[#A1A1AA] font-sans leading-relaxed text-sm" data-testid="today-state">
                      {todayMorning.output.reflection}
                    </p>
                  )}
                  {/* Feeling + Sleep pills */}
                  <div className="flex items-center flex-wrap gap-2 mt-3">
                    {todayMorning.feeling && (
                      <span className={`text-xs px-2.5 py-1 rounded-full font-sans capitalize ${
                        todayMorning.feeling === 'calm' ? 'bg-blue-500/15 text-blue-300' :
                        todayMorning.feeling === 'focused' ? 'bg-emerald-500/15 text-emerald-300' :
                        todayMorning.feeling === 'scattered' ? 'bg-amber-500/15 text-amber-300' :
                        todayMorning.feeling === 'heavy' ? 'bg-purple-500/15 text-purple-300' :
                        'bg-rose-500/15 text-rose-300'
                      }`} data-testid="today-feeling">{todayMorning.feeling}</span>
                    )}
                    {todayMorning.sleep_quality && (
                      <span className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-[#A1A1AA] font-sans" data-testid="today-sleep">
                        {language === 'tr' ? 'Uyku' : 'Sleep'}: {todayMorning.sleep_quality}/10
                      </span>
                    )}
                    {todayMorning.output?.daily_tone && (
                      <span className="text-xs px-2.5 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] font-sans" data-testid="today-tone">
                        {todayMorning.output.daily_tone.name}
                      </span>
                    )}
                  </div>
                </div>

                {/* Next Action */}
                {todayMorning.output?.micro_action && (
                  <div className="px-6 pb-5">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400/70 font-sans mb-1">
                      {language === 'tr' ? 'Sonraki Adım' : 'Next Action'}
                    </p>
                    <p className="text-[#D1D1D6] font-sans text-sm" data-testid="today-micro-action">
                      {todayMorning.output.micro_action}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              /* MORNING NOT DONE — Prompt to check in */
              <div
                className="rounded-2xl border border-dashed border-[#D4AF37]/30 bg-[#D4AF37]/5 p-6 cursor-pointer hover:border-[#D4AF37]/50 transition-all group"
                onClick={() => navigate('/morning')}
                data-testid="morning-cta"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center group-hover:bg-[#D4AF37]/30 transition-colors">
                      <Sunrise className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="font-serif text-lg text-[#F8F5F2]">
                        {language === 'tr' ? 'Günü Başlat' : 'Start Your Day'}
                      </h3>
                      <p className="text-xs text-[#6B7280] font-sans">
                        {language === 'tr' ? 'Sabah kalibrasyonunuz bugünün yönünü belirler' : 'Your morning calibration sets today\'s direction'}
                      </p>
                    </div>
                  </div>
                  <Play className="w-5 h-5 text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            )}
          </motion.div>

          {/* TODAY'S PATH — Daily Execution Navigator (Phase 2) */}
          {todayMorning?.submitted && (
            <TodayPath />
          )}

          {/* Sun Map Card — appears below the Today panel via natural flex order */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="glass-panel rounded-2xl p-6 lg:p-8 mb-6"
            data-testid="sun-map-card"
          >
            {/* Signal Bar — the map's voice, living inside the map */}
            <div className="-mx-2 mb-4 pb-3 border-b border-[#D4AF37]/10">
              <SignalBar />
            </div>
            <SunMap
              scores={assessment.scores}
              previousScores={previousAssessment?.scores}
              historyScores={history.slice(2)}
              hideTitle={true}
              hideActions={true}
              solarAlignmentScore={solarAlignment?.score}
              onExportPDF={handleExportPDF}
              onShare={handleShare}
              onUpdate={() => navigate('/assessment')}
              exporting={exporting}
              sharing={sharing}
            />
          </motion.div>

          {/* Active Missions Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="glass-panel rounded-2xl p-6 mb-6"
            data-testid="missions-section"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <Rocket className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-[#F8F5F2]">
                    {language === 'tr' ? 'Aktif Misyonlar' : 'Active Missions'}
                  </h3>
                  <p className="text-xs text-[#A1A1AA]">
                    {missionStats.completed}/{missionStats.total} {language === 'tr' ? 'tamamlandı' : 'completed'}
                    {missionStats.total > 0 && ` • +${Math.round(missionStats.completion_rate * 0.15)}% ${language === 'tr' ? 'hizalama bonusu' : 'alignment bonus'}`}
                  </p>
                </div>
              </div>
              <button
                  onClick={() => {
                    setShowAddMission(!showAddMission);
                    if (!showAddMission && missionSuggestions.length === 0) {
                      fetchMissionSuggestions();
                    }
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
                  data-testid="add-mission-btn"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">{language === 'tr' ? 'Ekle' : 'Add'}</span>
                </button>
            </div>

            {/* Mission List */}
            {missions.length > 0 ? (
              <div className="space-y-3">
                {missions.map((mission) => (
                  <motion.div
                    key={mission.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                      mission.status === 'completed'
                        ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30'
                        : 'bg-white/5 border-white/10 hover:border-[#D4AF37]/20'
                    }`}
                  >
                    <button
                      onClick={() => handleToggleMission(mission)}
                      className="mt-0.5 flex-shrink-0"
                      data-testid={`toggle-mission-${mission.id}`}
                    >
                      {mission.status === 'completed' ? (
                        <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                      ) : (
                        <Circle className="w-5 h-5 text-[#6B7280] hover:text-[#D4AF37]" />
                      )}
                    </button>
                    <button
                      onClick={() => navigate('/goals-checkin', { state: { mission: mission } })}
                      className="flex-1 min-w-0 text-left group"
                      data-testid={`mission-link-${mission.id}`}
                    >
                      <p className={`text-sm font-sans group-hover:text-[#D4AF37] transition-colors ${
                        mission.status === 'completed' ? 'text-[#A1A1AA] line-through' : 'text-[#F8F5F2]'
                      }`}>
                        {mission.text}
                      </p>
                      <p className="text-xs text-[#6B7280] mt-1 flex items-center gap-1.5">
                        {(DOMAIN_LABELS[language] || DOMAIN_LABELS.en)[mission.domain] || mission.domain}
                        {mission.frequency && mission.frequency !== 'weekly' && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-sans ${
                            mission.frequency === 'daily' ? 'bg-emerald-500/15 text-emerald-400' :
                            mission.frequency === 'monthly' ? 'bg-blue-500/15 text-blue-400' :
                            mission.frequency === 'recurring' ? 'bg-purple-500/15 text-purple-400' :
                            'bg-white/10 text-[#6B7280]'
                          }`}>{mission.frequency}</span>
                        )}
                        <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </p>
                    </button>
                    <button
                      onClick={() => handleDeleteMission(mission.id)}
                      className="text-[#6B7280] hover:text-red-400 transition-colors"
                      data-testid={`delete-mission-${mission.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-[#6B7280] text-sm mb-3">
                  {language === 'tr' ? 'Bu hafta henüz misyon yok' : 'No missions this week yet'}
                </p>
                <button
                  onClick={() => {
                    setShowAddMission(true);
                    fetchMissionSuggestions();
                  }}
                  className="text-[#D4AF37] text-sm hover:underline"
                >
                  {language === 'tr' ? 'İlk misyonunu ekle →' : 'Add your first mission →'}
                </button>
              </div>
            )}

            {/* Add Mission Panel */}
            <AnimatePresence>
              {showAddMission && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 pt-4 border-t border-white/10"
                >
                  {/* Suggestions */}
                  {missionSuggestions.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2">
                        {language === 'tr' ? 'Önerilen (en düşük alanlarınız)' : 'Suggested (your lowest domains)'}
                      </p>
                      <div className="space-y-2">
                        {missionSuggestions.map((suggestion, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleAddMission(suggestion.text, suggestion.domain)}
                            disabled={addingMission}
                            className="w-full text-left p-3 rounded-lg bg-black/40 border border-white/10 hover:border-[#D4AF37]/30 transition-all group"
                            data-testid={`suggestion-${idx}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-[#F8F5F2] group-hover:text-[#D4AF37]">
                                {suggestion.text}
                              </span>
                              <Plus className="w-4 h-4 text-[#6B7280] group-hover:text-[#D4AF37]" />
                            </div>
                            <span className="text-xs text-[#6B7280]">
                              {suggestion.domain_label} • {suggestion.score}/10
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Custom Mission Input */}
                  <div>
                    <p className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2">
                      {language === 'tr' ? 'Veya kendi misyonunu yaz' : 'Or write your own'}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={newMissionText}
                          onChange={(e) => setNewMissionText(e.target.value)}
                          placeholder={language === 'tr' ? 'Misyonunu yaz...' : 'Write your mission...'}
                          className="w-full px-4 py-2 pr-10 rounded-lg bg-black/40 border border-white/10 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none text-sm"
                          data-testid="new-mission-input"
                        />
                        <div className="absolute right-1 top-1">
                          <DictationButton 
                            onTranscript={(text) => setNewMissionText(prev => prev ? `${prev} ${text}` : text)}
                            className="!p-1.5"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <select
                          value={selectedDomain}
                          onChange={(e) => setSelectedDomain(e.target.value)}
                          className="flex-1 sm:flex-none px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-[#F8F5F2] focus:border-[#D4AF37]/50 focus:outline-none text-sm min-w-[120px]"
                          data-testid="mission-domain-select"
                        >
                          <option value="">{language === 'tr' ? 'Alan seç' : 'Select domain'}</option>
                          {Object.entries(DOMAIN_LABELS[language] || DOMAIN_LABELS.en).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </select>
                        <select
                          value={selectedFrequency}
                          onChange={(e) => setSelectedFrequency(e.target.value)}
                          className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-[#F8F5F2] focus:border-[#D4AF37]/50 focus:outline-none text-sm"
                          data-testid="mission-frequency-select"
                        >
                          <option value="daily">{language === 'tr' ? 'Günlük' : 'Daily'}</option>
                          <option value="weekly">{language === 'tr' ? 'Haftalık' : 'Weekly'}</option>
                          <option value="monthly">{language === 'tr' ? 'Aylık' : 'Monthly'}</option>
                          <option value="recurring">{language === 'tr' ? 'Tekrarlayan' : 'Recurring'}</option>
                        </select>
                        <button
                          onClick={() => handleAddMission(newMissionText, selectedDomain, selectedFrequency)}
                          disabled={!newMissionText.trim() || !selectedDomain || addingMission}
                          className="px-4 py-2 rounded-lg bg-[#D4AF37] text-[#030305] font-sans text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          data-testid="submit-mission-btn"
                        >
                          {addingMission ? <Loader2 className="w-4 h-4 animate-spin" /> : (language === 'tr' ? 'Ekle' : 'Add')}
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Carried Over Missions (collapsible) */}
            {carriedOverMissions.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <button
                  onClick={() => setShowCarriedOver(!showCarriedOver)}
                  className="w-full flex items-center justify-between text-left hover:opacity-80 transition-opacity"
                  data-testid="carried-over-toggle"
                >
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <span className="text-sm text-[#A1A1AA]">
                      {language === 'tr' 
                        ? `${carriedOverMissions.length} devam eden misyon` 
                        : `${carriedOverMissions.length} carried over`}
                    </span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-[#6B7280] transition-transform ${showCarriedOver ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {showCarriedOver && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-2 mt-3">
                        {carriedOverMissions.map((mission) => (
                          <motion.div
                            key={mission.id}
                            className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20"
                          >
                            <button
                              onClick={() => handleToggleMission(mission)}
                              className="w-5 h-5 rounded-full border-2 border-amber-500/50 flex items-center justify-center flex-shrink-0 hover:border-amber-500 transition-colors"
                              data-testid={`carried-over-toggle-${mission.id}`}
                            >
                              {mission.status === 'completed' && (
                                <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-sans ${mission.status === 'completed' ? 'text-[#6B7280] line-through' : 'text-[#F8F5F2]'}`}>
                                {mission.text}
                              </p>
                              <p className="text-xs text-amber-500/70">
                                {language === 'tr' ? 'Önceki haftadan' : 'From previous week'}
                              </p>
                            </div>
                            <button
                              onClick={() => handleDeleteMission(mission.id)}
                              className="p-1 rounded-lg text-[#6B7280] hover:text-red-400 hover:bg-red-500/10 transition-all"
                              data-testid={`delete-carried-over-${mission.id}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </motion.div>

          {/* Weekly Goals & Check-IN */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.47 }}
            className="flex gap-3 mb-6"
          >
            <button
              onClick={() => navigate('/goals-checkin')}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-white/5 border border-white/10 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/30 transition-all font-sans text-sm"
              data-testid="weekly-goals-btn"
            >
              <Target className="w-4 h-4" />
              {language === 'tr' ? 'Haftalık Hedefler' : 'Weekly Goals'}
            </button>
            <button
              onClick={() => navigate('/checkin')}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-white/5 border border-white/10 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/30 transition-all font-sans text-sm"
              data-testid="weekly-checkin-btn"
            >
              <RefreshCw className="w-4 h-4" />
              {language === 'tr' ? 'Haftalık Check-IN' : 'Weekly Check-IN'}
            </button>
          </motion.div>

          {/* Shadow Detection Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-panel rounded-2xl p-6 mb-6"
          >
            <button
              onClick={() => {
                if (!resistance && !resistanceLoading) {
                  fetchResistance();
                }
                setShowShadowPanel(!showShadowPanel);
              }}
              className="w-full flex items-center justify-between hover:opacity-80 transition-opacity"
              data-testid="shadow-panel-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-purple-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-serif text-lg text-[#F8F5F2]">
                    {language === 'tr' ? 'Gölge Tespiti' : 'Shadow Detection'}
                  </h3>
                  <p className="text-xs text-[#A1A1AA]">
                    {resistance 
                      ? (language === 'tr' ? 'Direnç kalıpları bulundu' : 'Resistance patterns detected')
                      : (language === 'tr' ? 'Kalıplarınızı keşfedin' : 'Discover your patterns')
                    }
                  </p>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 text-[#6B7280] transition-transform duration-300 ${showShadowPanel ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {showShadowPanel && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="pt-4 mt-4 border-t border-white/10 space-y-4">
                    {resistanceLoading ? (
                      <div className="py-8 text-center">
                        <Loader2 className="w-6 h-6 text-purple-400 animate-spin mx-auto mb-2" />
                        <p className="text-[#6B7280] text-sm font-sans">
                          {language === 'tr' ? 'Gölge kalıplarınız taranıyor...' : 'Scanning your shadow patterns...'}
                        </p>
                      </div>
                    ) : resistance ? (
                      <>
                        {/* Resistance Patterns */}
                        <div className="space-y-3">
                          {resistance.patterns?.map((pattern, idx) => {
                            const score = pattern.score || 5;
                            const intensity = score >= 7 ? 'high' : score >= 4 ? 'moderate' : 'low';
                            
                            return (
                              <motion.div
                                key={idx}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white/5 rounded-xl p-4"
                              >
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-purple-500/20">
                                    <AlertTriangle className="w-4 h-4 text-purple-400" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-[#F8F5F2] text-sm font-sans font-medium">
                                        {pattern.area}
                                      </span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-sans ${
                                        intensity === 'high' ? 'bg-red-500/20 text-red-400' :
                                        intensity === 'moderate' ? 'bg-amber-500/20 text-amber-400' :
                                        'bg-green-500/20 text-green-400'
                                      }`}>
                                        {score}/10
                                      </span>
                                    </div>
                                    <p className="text-[#A1A1AA] text-sm font-sans leading-relaxed">
                                      {pattern.pattern}
                                    </p>
                                    {pattern.shadow_message && (
                                      <p className="text-[#6B7280] text-xs font-sans mt-1 italic">
                                        "{pattern.shadow_message}"
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>

                        {/* Core Insight */}
                        {resistance.core_insight && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/20"
                          >
                            <p className="text-purple-200 text-sm font-sans leading-relaxed">
                              {resistance.core_insight}
                            </p>
                          </motion.div>
                        )}

                        {/* Shadow Message */}
                        {resistance.shadow_message && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="text-center py-2"
                          >
                            <p className="text-[#F8F5F2] text-sm font-serif italic">
                              "{resistance.shadow_message}"
                            </p>
                          </motion.div>
                        )}

                        {/* Reflection Question */}
                        {resistance.recommended_inquiry && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="pt-3 border-t border-white/10"
                          >
                            <p className="text-xs uppercase tracking-wider text-[#6B7280] font-sans mb-2">
                              {language === 'tr' ? 'Düşünme Sorusu' : 'Question for Reflection'}
                            </p>
                            <p className="text-[#D4AF37] text-base font-serif">
                              "{resistance.recommended_inquiry}"
                            </p>
                          </motion.div>
                        )}
                      </>
                    ) : (
                      <div className="py-6 text-center">
                        <Eye className="w-8 h-8 text-purple-400/50 mx-auto mb-3" />
                        <p className="text-[#A1A1AA] text-sm font-sans mb-2">
                          {language === 'tr' ? 'Gölge kalıplarınızı keşfedin' : 'Discover your shadow patterns'}
                        </p>
                        <p className="text-[#6B7280] text-xs font-sans">
                          {language === 'tr' 
                            ? 'Değerlendirmenize göre direnç kalıplarınız analiz edilecek' 
                            : 'Your resistance patterns will be analyzed based on your assessment'}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* AI Guidance Card (Inner Guide) — now also hosts Explore 12 Domains */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <AIGuidanceCard 
              analysis={analysis.analysis_text}
              archetypes={analysis.archetypes}
              priorities={analysis.priorities}
              weeklyFocus={analysis.weekly_focus}
              habits={analysis.habits}
              onRegenerate={handleRegenerateAnalysis}
              scores={assessment.scores}
              domainLabels={DOMAIN_LABELS[language] || DOMAIN_LABELS.en}
              onDomainClick={(key) => navigate(`/domain/${key}`)}
              isRegenerating={regenerating}
            />
          </motion.div>

        </div>
      </main>

      {/* Mobile Bottom Tab Bar */}
      <motion.nav
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-xl border-t border-white/10 z-50"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 40px)' }}
      >
        <div className="flex justify-around items-center py-3">
          {[
            { icon: Home, label: language === 'tr' ? 'Ana Sayfa' : 'Home', path: '/dashboard', active: true },
            { icon: Sparkles, label: language === 'tr' ? 'Navigatör' : 'Navigator', path: '/coaching' },
            { icon: Compass, label: language === 'tr' ? 'Ritüeller' : 'Rituals', path: '/integrate' },
            { icon: Library, label: 'INN', path: '/inn' },
            { icon: Settings, label: language === 'tr' ? 'Ayarlar' : 'Settings', path: '/settings' },
          ].map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center py-2 px-4 rounded-xl transition-all ${
                item.active 
                  ? 'text-[#D4AF37]' 
                  : 'text-[#6B7280] hover:text-[#D4AF37]'
              }`}
              data-testid={`mobile-tab-${item.path.slice(1)}`}
            >
              <item.icon className={`w-6 h-6 ${item.active ? 'text-[#D4AF37]' : ''}`} />
              <span className="text-xs mt-1 font-sans">{item.label}</span>
            </button>
          ))}
        </div>
      </motion.nav>
    </div>
  );
};

export default Dashboard;
